import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMaximize2, FiX } from "react-icons/fi";

/**
 * Displays a time-limited SAS annotated findings image (not persisted).
 */
export default function ImageAnalysisAnnotatedImage({ imageUrl, isRtl = false }) {
  const { t } = useTranslation("common");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setLoadError(false);
    setLightboxOpen(false);
  }, [imageUrl]);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxOpen, closeLightbox]);

  if (!imageUrl) return null;

  return (
    <>
      <div
        className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600"
        dir={isRtl ? "rtl" : "ltr"}
      >
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">
            {t("imageAnalysis.annotatedImageTitle")}
          </div>
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            disabled={loadError}
            className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 inline-flex items-center gap-1 disabled:opacity-50"
            title={t("imageAnalysis.viewAnnotatedImage")}
          >
            <FiMaximize2 className="w-3.5 h-3.5" />
            {t("imageAnalysis.viewAnnotatedImage")}
          </button>
        </div>
        {loadError ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t("imageAnalysis.annotatedImageLoadError")}
          </p>
        ) : (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="block w-full rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-gray-50 dark:bg-gray-900/50 focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B]"
          >
            <img
              src={imageUrl}
              alt={t("imageAnalysis.annotatedImageTitle")}
              className="w-full max-h-[280px] object-contain"
              onError={() => setLoadError(true)}
            />
          </button>
        )}
        <p className="mt-1.5 text-[10px] text-gray-400 dark:text-gray-500">
          {t("imageAnalysis.annotatedImageExpiry")}
        </p>
      </div>

      {lightboxOpen && !loadError && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t("imageAnalysis.annotatedImageTitle")}
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label={t("close")}
          >
            <FiX className="w-6 h-6" />
          </button>
          <img
            src={imageUrl}
            alt={t("imageAnalysis.annotatedImageTitle")}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
